import React, { FC } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { TrendingDown, TrendingUp } from '@material-ui/icons';

export type DayKpiTileTone = 'red' | 'white' | 'green';

export interface DayKpiTileData {
  /** Unique key (used for React key + sortable id). */
  key: string;
  /** Short metric label shown at the top of the tile. */
  label: string;
  /** Primary value for the day. */
  value: number;
  /** Unit / suffix appended to the primary value (e.g. '%', ' MIN'). */
  suffix: string;
  /** Optional pre-formatted secondary value (e.g. avg delay minutes for FCOTS). */
  secondaryValue?: string;
  secondarySuffix?: string;
  secondaryDescription?: string;
  /** Prior-day value used to compute the trend delta. */
  previousValue: number;
  /** Whether the previous value is meaningful. */
  previousAvailable?: boolean;
  /** True when lower values are better (e.g. delay, turnover). */
  inverseTrend?: boolean;
  /** When true, render an unavailable placeholder. */
  unavailable?: boolean;
  /** Visual tone for the primary numeric value. */
  tone: DayKpiTileTone;
  /** Visual tone for the secondary numeric value. Falls back to `tone`. */
  secondaryTone?: DayKpiTileTone;
  /** Optional inconspicuous note shown beneath the value (e.g. capacity context). */
  note?: string;
}

export interface DayKpiTileProps {
  data: DayKpiTileData;
}

const POSITIVE_COLOR = '#10b981';
const NEGATIVE_COLOR = '#ef4444';

const toneColor = (tone: DayKpiTileTone): string => {
  switch (tone) {
    case 'red':
      return NEGATIVE_COLOR;
    case 'green':
      return POSITIVE_COLOR;
    default:
      return '#f8fafc';
  }
};

const formatValue = (value: number, suffix: string): string => {
  if (suffix.includes('MIN')) return String(Math.round(value));
  return String(Math.round(value * 10) / 10);
};

const DayKpiTile: FC<DayKpiTileProps> = ({ data }) => {
  const classes = useStyles();

  const primaryColor = data.unavailable ? 'rgba(255,255,255,0.25)' : toneColor(data.tone);
  const secondaryColor = toneColor(data.secondaryTone ?? data.tone);

  const diff = data.value - data.previousValue;
  const hasPrev = data.previousAvailable !== false && data.previousValue !== 0;
  const pctChange = hasPrev ? (diff / Math.abs(data.previousValue)) * 100 : 0;
  const isPositive = diff > 0;
  const trendIsGood = data.inverseTrend ? !isPositive : isPositive;
  const trendColor = trendIsGood ? POSITIVE_COLOR : NEGATIVE_COLOR;

  return (
    <Box className={classes.tile}>
      <Typography className={classes.label}>{data.label}</Typography>

      <Box className={classes.valueRow}>
        <Typography className={classes.value} style={{ color: primaryColor }}>
          {data.unavailable ? '—' : formatValue(data.value, data.suffix)}
          {!data.unavailable && data.suffix && <span className={classes.unit}>{data.suffix}</span>}
        </Typography>
        {!data.unavailable && data.secondaryValue && (
          <Typography
            className={classes.secondaryValue}
            style={{ color: secondaryColor }}
            title={data.secondaryDescription}
          >
            {data.secondaryValue}
            {data.secondarySuffix && <span className={classes.unit}>{data.secondarySuffix}</span>}
          </Typography>
        )}
      </Box>

      {!data.unavailable && data.note && <Typography className={classes.note}>{data.note}</Typography>}

      <Box className={classes.footer}>
        {data.unavailable ? (
          <Typography className={classes.footerMuted}>No data for this day</Typography>
        ) : !hasPrev ? (
          <Typography className={classes.footerMuted}>No previous day</Typography>
        ) : (
          <>
            {isPositive ? (
              <TrendingUp style={{ fontSize: 28, color: trendColor, marginRight: 8 }} />
            ) : (
              <TrendingDown style={{ fontSize: 28, color: trendColor, marginRight: 8 }} />
            )}
            <Typography className={classes.footerTrend} style={{ color: trendColor }}>
              {isPositive ? '+' : ''}
              {pctChange.toFixed(1)}%
            </Typography>
            <Typography className={classes.footerVs}>vs previous day</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  tile: {
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    borderRadius: 6,
    padding: theme.spacing(3, 3.25),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    height: '100%',
    minHeight: 220,
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 1,
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.04)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2.5, 2.75),
      minHeight: 190,
    },
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.16em',
    color: 'rgba(255, 255, 255, 0.6)',
    textTransform: 'uppercase',
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(2.5),
    flex: 1,
    flexWrap: 'wrap',
  },
  value: {
    fontSize: '3.75rem',
    fontWeight: 800,
    lineHeight: 1,
    letterSpacing: '-0.03em',
    [theme.breakpoints.down('md')]: {
      fontSize: '3rem',
    },
  },
  secondaryValue: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '-0.01em',
    opacity: 0.9,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.6rem',
    },
  },
  unit: {
    fontSize: '0.45em',
    fontWeight: 500,
    marginLeft: 4,
    opacity: 0.6,
  },
  note: {
    fontSize: '0.72rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.35)',
    letterSpacing: '0.01em',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  },
  footerTrend: {
    fontSize: '1.5rem',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.25rem',
    },
  },
  footerVs: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.45)',
    marginLeft: 10,
    letterSpacing: '0.02em',
  },
  footerMuted: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.35)',
  },
}));

export default DayKpiTile;
